import React, { useState, useRef, useEffect } from 'react';
import CustomButton from '../../../../components/CustomButton';
import CustomText, {
    getFontSize,
    getFontWeight
} from '../../../../components/CustomText';
import './OTPInput.css';
import { makeStyles, useTheme, withStyles } from "@material-ui/core/styles";
import { useHistory } from 'react-router-dom';
import CustomBoardNewLogin from '../../../../components/CustomBoardNewLogin';
import * as selector from '../../../../../common/selectors';
import * as authAction from '../../../../../common/actions/auth';
import * as authSelector from '../../../../../common/selectors/auth';
import AuthManager from '../../../../../utils/AuthManager';
import { useSelector, useDispatch } from 'react-redux';
import CustomToast from '../../../../components/CustomToast';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import { Grid } from '@material-ui/core';
import Image from "../../../../../assets";

const useStyles = makeStyles((theme) => ({
    container: {
        paddingTop: "3.5rem",
        paddingLeft: '2.2rem',
        paddingRight: '2.2rem',
        [theme.breakpoints.down('sm')]: {
            paddingTop: "0rem",
            paddingLeft: '1rem',
            paddingRight: '1rem',
            paddingBottom: '2rem'
        },
    },
    headerTextStyle: {
        fontWeight: 700,
        fontSize: 20,
        color: "#333333",
        [theme.breakpoints.down('sm')]: {
            fontSize: 18,
        },
        marginTop: 20
    },
    subHeaderText: {
        fontWeight: 500,
        fontSize: 14,
        color: '#333333',
        marginTop: '0.6rem',
        [theme.breakpoints.down('sm')]: {
            fontSize: 13,
        },
    },
    otpContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '13px',
        justifyContent: 'flex-start',
        marginTop: '2.5rem',
        [theme.breakpoints.down('xs')]: {
            gap: '11px',
        },
    },
    otpInput: {
        textAlign: 'center',
        fontSize: '16px',
        width: '48px',
        height: '58px',
        border: '1px solid #C6C7C9',
        [theme.breakpoints.down('xs')]: {
            width: '37px',
            height: '50px',
        },
        borderRadius: '8px',
        '&:focus': {
            outline: 'none',
            border: '2px solid #0EA5E9'
        }
    },
    resendText: {
        color: '#0D99FF',
        fontSize: '12px',
        cursor: 'pointer',
        marginTop: '1rem',
        fontWeight: 500,
        marginLeft: '0.3rem'
    },
    resendTextV1: {
        color: 'rgba(112, 112, 112, 1)',
        fontSize: '12px',
        cursor: 'pointer',
        marginTop: '1rem',
        fontWeight: 500,
    },
    buttonStyle: {
        height: '48px',
        [theme.breakpoints.down('sm')]: {
            height: '45px',
        },
        [theme.breakpoints.up("lg")]: {
            minWidth: 496,
            maxWidth: 496,
            left: '-0rem',
        },
        [theme.breakpoints.down("sm")]: {
            minWidth: 410,
            maxWidth: 410,
        },
        [theme.breakpoints.down('xs')]: {
            minWidth: '100%',
            maxWidth: '100%',
            left: 0,
        }
    },
}));

const LoginOTP = () => {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useDispatch();
    
    const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
    const [currentFocus, setCurrentFocus] = useState(0);
    const inputRefs = useRef([]);
    const [customToastStatus, setCustomToastStatus] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [twoFAReferenceCode, setTwoFAReferenceCode] = useState('');
    const [mobile, setMobile] = useState('');
    const [otpCountDown, setOtpCountDown] = useState(60);
    const timerRef = useRef(null);
    const [loginSubmitData, setLoginSubmitData] = useState(null);

    // Redux selectors
    const loginData = useSelector(selector.getLoginData);
    const loginLoading = useSelector(selector.getLoginLoading);
    const generateLoginOtpData = useSelector(selector.generateLoginOtpData);
    const generateLoginOtpLoading = useSelector(selector.generateLoginOtpLoading);

    // Focus correct input whenever currentFocus changes
    useEffect(() => {
        if (inputRefs.current[currentFocus]) {
            inputRefs.current[currentFocus].focus();
        }
    }, [currentFocus]);

    const startTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }
        setOtpCountDown(60);
        timerRef.current = setInterval(() => {
            setOtpCountDown((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    timerRef.current = null;
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    useEffect(() => {
        startTimer();
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, []);

    // Retrieve data from localStorage
    useEffect(() => {
        const storedTwoFAReferenceCode = localStorage.getItem('TwoFAReferenceCode');
        const storedMobile = localStorage.getItem('Mobile');
        const storedLoginSubmitData = localStorage.getItem('loginSubmitData');

        if (storedTwoFAReferenceCode && storedMobile && storedLoginSubmitData) {
            setTwoFAReferenceCode(storedTwoFAReferenceCode);
            setMobile(storedMobile);
            setLoginSubmitData(JSON.parse(storedLoginSubmitData));
        } else {
            // If no data found, redirect back to login
            history.push('/');
        }
    }, []);

    // Handle login response
    useEffect(() => {
        if (loginData && !loginLoading) {
            const loginSuccess = loginData.success || loginData.Success;
            const loginDataData = loginData.data || loginData.Data || loginData;

            if (loginSuccess === false) {
                const errorMessage = loginData.message || loginData.Message || loginDataData.Message;
                setCustomToastStatus(true);
                setToastMessage(errorMessage || 'Login failed. Please try again.');
            } else if (loginSuccess === true) {
                const sessionID = loginDataData.session_id || loginDataData.SessionId;
                const needSetUserID = loginDataData.need_set_user_id || loginDataData.NeedSetUserId;
                const isMasterAccount = loginDataData.is_master_account || loginDataData.IsMasterAccount;
                const isDirectClient = loginDataData.direct_client || loginDataData.DirectClient;
                const sessionExpiryWarning = loginDataData.session_expiry_warning || loginDataData.SessionExpiryWarning;
                const sessionExpiryEnd = loginDataData.session_expiry_end || loginDataData.SessionExpiryEnd;
                const token = loginDataData.token || loginDataData.Token;

                AuthManager.setNeedChangeUserID(needSetUserID);
                AuthManager.setToken(token);
                AuthManager.setSessionID(sessionID);
                AuthManager.setSetChangeUserID(needSetUserID);
                AuthManager.setIsMasterAccount(isMasterAccount);
                AuthManager.setDirectClient(isDirectClient);
                AuthManager.setExpiryWarning(sessionExpiryWarning);
                AuthManager.setExpiryEnd(sessionExpiryEnd);

                // Clear localStorage
                localStorage.removeItem('TwoFAReferenceCode');
                localStorage.removeItem('Mobile');
                localStorage.removeItem('loginSubmitData');

                window.location.href = process.env.REACT_APP_TIQ_BASE_URL;
            }
        }
    }, [loginData, loginLoading]);

    // OTP Input Handlers
    const handleInputChange = (index, value) => {
        let digit = value.replace(/\D/g, '').slice(-1);
        const newOtpValues = [...otpValues];
        newOtpValues[index] = digit;
        setOtpValues(newOtpValues);
        
        if (digit && index < otpValues.length - 1) {
            setCurrentFocus(index + 1);
        }
    };

    const handleKeyDown = (index, event) => {
        // Existing Backspace logic (unchanged)
        if (event.key === 'Backspace') {
            if (!otpValues[index] && index > 0) {
                setCurrentFocus(index - 1);
            } else {
                const newOtpValues = [...otpValues];
                newOtpValues[index] = '';
                setOtpValues(newOtpValues);
            }
        }
    
        // Approach 1: Submit only when Enter is pressed on the LAST field
        if (event.key === 'Enter' && index === otpValues.length - 1) {
            event.preventDefault(); // important if inside a <form>
            onSubmitOTP();
        }
    };

    // Resend OTP
    const onClickResend = async () => {
        if (!loginSubmitData) {
            setCustomToastStatus(true);
            setToastMessage('Login data not found. Please try again.');
            return;
        }

        try {
            const recaptchaToken = await window.grecaptcha.execute(
                "6Lce7OMZAAAAALxOm9WwIETw1EL9UB73SDe4g0Tx",
                { action: "WEB_LOGIN" }
            );
            
            if (!recaptchaToken) {
                setCustomToastStatus(true);
                setToastMessage("Unable to verify reCAPTCHA. Please try again.");
                return;
            }

            const submitData = {
                ...loginSubmitData,
                RecaptchaToken: recaptchaToken
            };

            dispatch(authAction.generateLoginOtp(submitData));
            startTimer();
            setOtpValues(['', '', '', '', '', '']);
        } catch (error) {
            setCustomToastStatus(true);
            setToastMessage("Error during reCAPTCHA verification. Please try again.");
        }
    };

    // Handle resend OTP response
    useEffect(() => {
        if (generateLoginOtpData && !generateLoginOtpLoading) {
            const otpSuccess = generateLoginOtpData.success || generateLoginOtpData.Success;
            const otpData = generateLoginOtpData.data || generateLoginOtpData.Data;
            
            if (otpSuccess === true && otpData) {
                const newTwoFAReferenceCode = otpData.two_fa_reference_code;
                const newMobile = otpData.mobile;
                
                setTwoFAReferenceCode(newTwoFAReferenceCode);
                setMobile(newMobile);
                
                localStorage.setItem('TwoFAReferenceCode', newTwoFAReferenceCode);
                localStorage.setItem('Mobile', newMobile);
            }
        }
    }, [generateLoginOtpData, generateLoginOtpLoading]);

    // Verify OTP and Login
    const onSubmitOTP = async () => {
        const code = otpValues.join('');
        
        if (!code || code.length !== 6) {
            setCustomToastStatus(true);
            setToastMessage('Please enter a valid 6-digit OTP.');
            return;
        }

        if (!loginSubmitData) {
            setCustomToastStatus(true);
            setToastMessage('Login data not found. Please try again.');
            return;
        }

        try {
            // Generate new recaptcha token for login request
            const recaptchaToken = await window.grecaptcha.execute(
                "6Lce7OMZAAAAALxOm9WwIETw1EL9UB73SDe4g0Tx",
                { action: "WEB_LOGIN" }
            );

            if (!recaptchaToken) {
                setCustomToastStatus(true);
                setToastMessage("Unable to verify reCAPTCHA. Please try again.");
                return;
            }

            // Call postLoginRequest with Code and ReferenceNumber
            dispatch(authAction.postLoginRequest(
                loginSubmitData.UserID,
                loginSubmitData.Password,
                loginSubmitData.AuthClientId,
                loginSubmitData.AuthRedirectUrl,
                loginSubmitData.AuthState,
                recaptchaToken,
                code,
                twoFAReferenceCode
            ));
        } catch (error) {
            setCustomToastStatus(true);
            setToastMessage("Error during reCAPTCHA verification. Please try again.");
        }
    };

    const handleCloseCustomToast = (event, reason) => {
        if (reason === 'clickaway') return;
        setCustomToastStatus(false);
    };

    const handleBackToHome = (event, reason) => {
         // Clear localStorage
         localStorage.removeItem('TwoFAReferenceCode');
         localStorage.removeItem('Mobile');
         localStorage.removeItem('loginSubmitData');
        history.push('/');
    };

    const renderPage = () => {
        const maskedMobile = mobile ? `****${mobile.slice(-4)}` : '****';
        
        return (
            <div className={classes.container}>
                <div style={{ display: 'flex', flexDirection: "row", justifyContent: 'center' }}>
                    <img src={Image.otpIcon} alt="OTP Icon" />
                </div>
                <div className={classes.headerTextStyle}>We've sent an OTP code to</div>
                <div className={classes.subHeaderText}>
                    {maskedMobile}
                </div>
                <div className={classes.otpContainer}>
                    {otpValues.map((val, index) => (
                        <input
                            key={index}
                            ref={(el) => (inputRefs.current[index] = el)}
                            type="text"
                            maxLength={1}
                            value={val}
                            onChange={(e) => handleInputChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            inputMode="numeric"
                            className={classes.otpInput}
                        />
                    ))}
                </div>
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start' }}>
                    <span className={classes.resendTextV1}> Didn't receive the OTP? </span>
                    <span 
                        className={classes.resendText} 
                        onClick={otpCountDown < 1 ? onClickResend : null}
                        style={{ cursor: otpCountDown > 0 ? 'not-allowed' : 'pointer' }}
                    >
                        {otpCountDown > 0 ? `Resend in ${otpCountDown} sec` : 'Resend OTP'}
                    </span>
                </div>
                <div style={{ marginTop: '2.5rem' }}>
                    <CustomButton
                        title="Verify"
                        onClick={onSubmitOTP}
                        design={classes.buttonStyle}
                    />
                </div>
                <div className={classes.buttonStyle} onClick={handleBackToHome}  style={{ marginTop: '2rem',justifyContent:'center',width:'100%',display:'flex',cursor:'pointer',color:'#0D99FF' }}>
                    <span>Back</span>
                </div>
            </div>
        );
    };

    const renderMaintenanceChild = () => <></>;

    const height = 800;
    const width = 600;

    return (
        <>
            <CustomBoardNewLogin
                maintenanceChild={renderMaintenanceChild()}
                children={renderPage()}
                height={height}
                width={width}
            />
            <CustomToast
                open={customToastStatus}
                handleCloseCustomToast={handleCloseCustomToast}
                title={toastMessage}
                warning
            />
            {(loginLoading || generateLoginOtpLoading) && (
                <LoadingOverlay loading={true} />
            )}
        </>
    );
};

export default LoginOTP;
